import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const WhatsAppHome = () => {
  useEffect(() => {
    document.title = "Sendd — WhatsApp automation for developers";
    if (typeof window !== 'undefined' && (window as any).$) {
      (window as any).$(".pagetitle").html("Sendd");
    }
  }, []);

  return (
    <>
      {/* ─── Hero ─── */}
      <section id="hero" className="hero">
        <div className="container hero-inner">
          <span className="hero-badge">
            <span className="hero-badge-dot"></span>
            v1.0 · Public beta
          </span>

          <h1>WhatsApp automation,<br />in <em>three lines</em> of code.</h1>

          <p className="hero-sub">
            A simple REST API to send WhatsApp messages from your own number.
            No Meta verification. No template approvals. No business manager.
          </p>

          <div className="hero-ctas">
            <Link to="/login" className="btn btn-dark">Get an API key →</Link>
            <a href="#pricing" className="btn btn-ghost">See Pricing</a>
          </div>

          <div className="hero-meta">
            <span>3-minute setup</span>
            <span className="dot-sep">·</span>
            <span>Simple REST API</span>
            <span className="dot-sep">·</span>
            <span>Flat INR Pricing</span>
          </div>

          <div className="hero-code">
            <div className="hero-code-header">
              <span className="hero-code-lang">curl</span>
              <span className="hero-code-meta">POST /sendwhatsappmessage</span>
            </div>
            <pre className="hero-code-body">{`# Send a WhatsApp message — that's the whole thing.
curl https://api.sendd.dev/sendwhatsappmessage \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY_HERE" \\
  -d '{
    "senderphonenumber": "919876543210",
    "receiverphonenumber": [{ "phonenumber": "919812345678" }],
    "message": "Your order #1234 has shipped 📦"
  }'

# → 200 OK   { "status": "queued" }`}</pre>
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section id="how" className="section section--alt">
        <div className="container">
          <span className="eyebrow">// How it works</span>
          <h2>From zero to first message <em>in three minutes.</em></h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-num">01 — Sign up</div>
              <h3>Make an account</h3>
              <p>Create an account and grab your API key from the dashboard. No credit card required.</p>
            </div>
            <div className="step">
              <div className="step-num">02 — Connect</div>
              <h3>Scan a QR</h3>
              <p>Link your WhatsApp number once by scanning a QR code, just like WhatsApp Web.</p>
            </div>
            <div className="step">
              <div className="step-num">03 — Send</div>
              <h3>POST a message</h3>
              <p>One HTTP call from your code, cron job, or backend fires off a WhatsApp message.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pricing Section (Reusing steps-grid layout) ─── */}
      <section id="pricing" className="section">
        <div className="container">
          <span className="eyebrow">// Products &amp; Pricing</span>
          <h2>Flat, transparent <em>INR pricing</em></h2>
          <div className="steps-grid">
            <div className="step">
              <div className="step-num">₹2.00 / msg</div>
              <h3>WhatsApp Delivery Credit</h3>
              <p>Programmatic message delivery via REST API. Minimum purchase quantity is <strong>₹100 INR</strong>. Credits never expire.</p>
              <br />
              <Link to="/login" className="btn btn-dark">Buy Credits →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Honest note (Fixed Alignment Here) ─── */}
      <section id="about" className="section section--alt">
        <div className="container"> {/* Changed from honest-wrapper to standard container */}
          <span className="eyebrow">// A note on what this is</span>
          <div className="honest-card">
            <p>Sendd is built on top of your existing WhatsApp account — the same way WhatsApp Web is. It's <strong>not</strong> the official WhatsApp Business API, and we're not affiliated with WhatsApp or Meta.</p>
            <p>Best for: personal alerts, internal tools, side projects, transactional 1:1 messages.</p>
            <p>Not for: unsolicited marketing, bulk broadcasts, anything that would get a normal WhatsApp account banned.</p>
          </div>
        </div>
      </section>

      {/* ─── Minimal Legal & Compliance Section ─── */}
      <section id="legal" className="section">
        <div className="container">
          <span className="eyebrow">// Legal and Compliance</span>
          <h2>Terms, Refunds and Contact</h2>
          
          <div className="steps-grid">
            <div id="terms" className="step">
              <h3>Terms and Conditions</h3>
              <ul>
                  <li>You maintain sole accountability for all message content pushed via your credentials.</li>
                  <li>Our service uses an ongoing connection protocol simulation. We are completely unaffiliated with Meta/WhatsApp.</li>
                  <li>Usage of these routing instances for sending unsolicited spam, automated marketing blasts, or malicious scripts is strictly prohibited. Your integration token will be dropped immediately if flagged.</li>
                  <li>If your account / messages are flagged for any reason, no refund will be issued. </li>
              </ul>
            </div>

            <div id="refunds" className="step">
              <h3>Refunds and Cancellations</h3>
              <ul style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
                <li>All automated transaction top-ups (Minimum INR 100) are final once processed.</li>
                <li><strong>Refund Exception:</strong> Financial compensation for remaining un-utilized API credits will be executed only if senddit.in explicitly decides to discontinue the execution of its system service.</li>
              </ul>
            </div>

            <div id="contact" className="step">
              <h3>Contact Us</h3>
              <p>
                <strong>Email:</strong> senddit@gmail.com<br />
                <strong>Address:</strong> Balewadi, Pune, MH - 422007
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="final-cta">
        <div className="container">
          <h2>Send your first message in the <em>next ten minutes.</em></h2>
          <p>Free to try. No credit card. No sales call.</p>
          <Link to="/login" className="btn btn-dark">Get your API key →</Link>
        </div>
      </section>

      {/* ─── Minimal Footer Links ─── */}
      <footer className="section section--alt">

      </footer>
    </>
  );
};

export default WhatsAppHome;